import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { MessageService } from '../service-ui/message.service';
import { StartupService } from '../service-ui/startup.service';

// =====================================================================
// Types — mirror docs/contracts/shared/SalesTaxExemption.md
// =====================================================================

export type SalesTaxExemptionStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'INFO_REQUESTED'
  | 'REVOKED';

export type SalesTaxExemptionType = 'wholesale' | 'other' | 'non-exempt';

export type SalesTaxExemptionAction = 'ACCEPT' | 'REJECT' | 'REQUEST_INFO' | 'REVOKE';

export type SalesTaxExemptionMessageFrom = 'organization' | 'serviceprovider' | 'network';

export interface SalesTaxExemptionCertificate {
  number: string;
  issuingState: string;
  fileKey: string;
  fileMimeType: string;
  fileSize: number;
  fileName: string;
  uploadedAt: string;
}

export interface SalesTaxExemptionMessage {
  from: SalesTaxExemptionMessageFrom;
  userId: number;
  userName: string;
  at: string;
  text: string;
}

export interface SalesTaxExemptionReviewer {
  userId: number;
  userName: string;
  role: SalesTaxExemptionMessageFrom;
}

/**
 * Full exemption blob stored in `organization.salesTaxExemptionData`.
 *
 * Vendor-neutral note: the client is intentionally agnostic to whichever
 * downstream sales-tax service the server is using (could change per
 * jurisdiction, could be multiple). Field names below mirror the wire
 * payload the server emits.
 */
export interface SalesTaxExemption {
  status: SalesTaxExemptionStatus;
  type: SalesTaxExemptionType;
  certificate: SalesTaxExemptionCertificate | null;
  exemptStates: string[];
  messages: SalesTaxExemptionMessage[];
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: SalesTaxExemptionReviewer | null;
  /** True if the last successful sync to the downstream sales-tax service mirrored the current DB state. */
  salesTaxServiceSynced: boolean;
  /** States last successfully reflected in the downstream sales-tax service. */
  salesTaxServiceSyncedStates: string[];
  /** Exemption type last successfully reflected in the downstream sales-tax service. */
  salesTaxServiceSyncedType: SalesTaxExemptionType | null;
  /** ISO timestamp of the last successful sync to the downstream sales-tax service. */
  salesTaxServiceSyncedAt: string | null;
  /** Last error message from the downstream sales-tax service, if any. */
  salesTaxServiceLastError: string | null;
  /**
   * Transient ~5-minute presigned GET URL for the certificate file,
   * minted by the server on every read so the UI can render without
   * a second round trip. May be absent.
   */
  certificateUrl?: string;
}

/** Body of `PUT /salestax/exemption/:orgId`. */
export interface SaveSalesTaxExemptionRequest {
  type: SalesTaxExemptionType;
  certificate: Omit<SalesTaxExemptionCertificate, 'uploadedAt'> | null;
  exemptStates: string[];
  message?: string;
}

/** Body of `POST /salestax/exemption/:orgId/upload-url`. */
export interface SalesTaxUploadUrlRequest {
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

/** Response of `POST /salestax/exemption/:orgId/upload-url`. */
export interface SalesTaxUploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
  expiresAt: string;
  requiredHeaders?: { [name: string]: string };
}

/** Response of `POST /salestax/exemption/:orgId/download-url`. */
export interface SalesTaxDownloadUrlResponse {
  downloadUrl: string;
  expiresAt: string;
  mimeType: string;
}

/** Body of `POST /salestax/exemption/:orgId/review`. */
export interface SalesTaxReviewRequest {
  action: SalesTaxExemptionAction;
  message?: string;
  acceptedType?: 'wholesale' | 'other';
  acceptedStates?: string[];
}

export interface SalesTaxReviewWarning {
  code: string;
  message: string;
}

export interface SalesTaxExemptionResponse {
  exemption: SalesTaxExemption | null;
  warnings?: SalesTaxReviewWarning[];
}

export interface SalesTaxQueueItem {
  orgId: number;
  orgName: string;
  status: SalesTaxExemptionStatus;
  type: SalesTaxExemptionType;
  submittedAt: string;
  certificateMimeType?: string;
  exemptStatesCount: number;
}

export interface SalesTaxConfig {
  nexusStates: string[];
  reciprocityOther: string[];
  reciprocityWholesale: string[];
  updatedAt: string | null;
  updatedBy: SalesTaxExemptionReviewer | null;
}

export interface SaveSalesTaxConfigRequest {
  nexusStates: string[];
  reciprocityOther: string[];
  reciprocityWholesale: string[];
}

/** MIME types accepted for the certificate upload. */
export const SALES_TAX_ACCEPTED_MIME_TYPES: ReadonlyArray<string> = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'application/pdf',
];

/** Max file size for a certificate upload (10 MB). */
export const SALES_TAX_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// =====================================================================
// Service
// =====================================================================

/**
 * Sales-Tax Exemption client service.
 *
 * Intentionally does NOT extend `DataService` and does NOT self-register
 * with `StartupService` — the exemption blob and the SP config blob are
 * loaded on demand, never at startup, mirroring the existing
 * {@link SalesTaxService} style.
 *
 * Errors are NOT swallowed via a global handler — they propagate so the
 * calling component can render them inline (per the team's recent
 * convention introduced for Change Packages / Split Print Node Order).
 *
 * See `docs/contracts/shared/SalesTaxExemption.md` for the full
 * contract.
 */
@Injectable()
export class SalesTaxExemptionService {

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private startupService: StartupService
  ) {}

  // ── Organization exemption blob ────────────────────────────────────

  /** GET /salestax/exemption/:orgId */
  getExemption(orgId: number): Observable<SalesTaxExemption | null> {
    const url = `${environment.apiBaseURL}salestax/exemption/${orgId}`;
    this.log(`getExemption org=${orgId}`);
    return this.http.get<SalesTaxExemptionResponse>(url, this.startupService.getHttpOptions())
      .pipe(
        map(r => (r && r.exemption) ? r.exemption : null),
        tap(_ => this.log(`got exemption for org=${orgId}`))
      );
  }

  /** PUT /salestax/exemption/:orgId — organization-side write */
  saveExemption(orgId: number, body: SaveSalesTaxExemptionRequest): Observable<SalesTaxExemptionResponse> {
    const url = `${environment.apiBaseURL}salestax/exemption/${orgId}`;
    this.log(`saveExemption org=${orgId} type=${body.type}`);
    return this.http.put<SalesTaxExemptionResponse>(url, body, this.startupService.getHttpOptions());
  }

  /** POST /salestax/exemption/:orgId/upload-url — request a presigned PUT URL */
  requestUploadUrl(orgId: number, file: File): Observable<SalesTaxUploadUrlResponse> {
    const url = `${environment.apiBaseURL}salestax/exemption/${orgId}/upload-url`;
    const body: SalesTaxUploadUrlRequest = {
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
    };
    this.log(`requestUploadUrl org=${orgId} name=${file.name} bytes=${file.size}`);
    return this.http.post<SalesTaxUploadUrlResponse>(url, body, this.startupService.getHttpOptions());
  }

  /**
   * Upload the certificate file directly to S3 using the presigned URL.
   * Returns the raw HTTP event stream so the caller can render progress.
   * NOTE: this request goes to S3, not our API — do NOT attach the
   * Cognito auth headers (S3 will reject the request if it sees them
   * alongside the presigned signature).
   */
  uploadCertificate(
    uploadUrl: string,
    file: File,
    requiredHeaders?: { [name: string]: string }
  ): Observable<HttpEvent<any>> {
    let headers = new HttpHeaders();
    if (requiredHeaders) {
      for (const k of Object.keys(requiredHeaders)) {
        headers = headers.set(k, requiredHeaders[k]);
      }
    } else {
      headers = headers.set('Content-Type', file.type || 'application/octet-stream');
    }
    const req = new HttpRequest('PUT', uploadUrl, file, {
      headers,
      reportProgress: true,
    });
    return this.http.request(req);
  }

  /** POST /salestax/exemption/:orgId/download-url */
  requestDownloadUrl(orgId: number): Observable<SalesTaxDownloadUrlResponse> {
    const url = `${environment.apiBaseURL}salestax/exemption/${orgId}/download-url`;
    return this.http.post<SalesTaxDownloadUrlResponse>(url, {}, this.startupService.getHttpOptions());
  }

  /** POST /salestax/exemption/:orgId/review */
  review(orgId: number, body: SalesTaxReviewRequest): Observable<SalesTaxExemptionResponse> {
    const url = `${environment.apiBaseURL}salestax/exemption/${orgId}/review`;
    this.log(`review org=${orgId} action=${body.action}`);
    return this.http.post<SalesTaxExemptionResponse>(url, body, this.startupService.getHttpOptions());
  }

  /** GET /salestax/exemption?serviceProviderId=...&status=... */
  getReviewQueue(serviceProviderId?: number, statuses?: SalesTaxExemptionStatus[]):
      Observable<SalesTaxQueueItem[]> {
    const params: string[] = [];
    if (serviceProviderId != null) {
      params.push(`serviceProviderId=${encodeURIComponent(String(serviceProviderId))}`);
    }
    if (statuses && statuses.length) {
      params.push(`status=${encodeURIComponent(statuses.join(','))}`);
    }
    const qs = params.length ? `?${params.join('&')}` : '';
    const url = `${environment.apiBaseURL}salestax/exemption${qs}`;
    this.log(`getReviewQueue sp=${serviceProviderId} statuses=${(statuses || []).join(',')}`);
    return this.http.get<{ items: SalesTaxQueueItem[] }>(url, this.startupService.getHttpOptions())
      .pipe(
        map(r => (r && r.items) ? r.items : []),
        catchError(err => {
          // Read-only list — degrade gracefully so the rest of the UI stays usable.
          console.error('SalesTaxExemptionService.getReviewQueue failed', err);
          return of([] as SalesTaxQueueItem[]);
        })
      );
  }

  // ── ServiceProvider config ─────────────────────────────────────────

  /** GET /salestax/serviceprovider/:spId/config */
  getSpConfig(spId: number): Observable<SalesTaxConfig> {
    const url = `${environment.apiBaseURL}salestax/serviceprovider/${spId}/config`;
    this.log(`getSpConfig sp=${spId}`);
    return this.http.get<{ config: SalesTaxConfig | null }>(url, this.startupService.getHttpOptions())
      .pipe(
        map(r => (r && r.config) ? r.config : this.defaultSpConfig()),
        tap(_ => this.log(`got salesTaxConfig for sp=${spId}`))
      );
  }

  /** PUT /salestax/serviceprovider/:spId/config */
  saveSpConfig(spId: number, body: SaveSalesTaxConfigRequest): Observable<SalesTaxConfig> {
    const url = `${environment.apiBaseURL}salestax/serviceprovider/${spId}/config`;
    this.log(`saveSpConfig sp=${spId}`);
    return this.http.put<{ config: SalesTaxConfig }>(url, body, this.startupService.getHttpOptions())
      .pipe(map(r => r.config));
  }

  private defaultSpConfig(): SalesTaxConfig {
    return {
      nexusStates: [],
      reciprocityOther: [],
      reciprocityWholesale: [],
      updatedAt: null,
      updatedBy: null,
    };
  }

  private log(message: string) {
    this.messageService.add('SalesTaxExemptionService: ' + message);
  }
}










